import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Plus, Camera, Globe, Store, Edit2, Loader2, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSAdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, pos_only, online, low_stock
  const [updating, setUpdating] = useState(null);
  const token = localStorage.getItem('pos_admin_token');

  useEffect(() => {
    fetchProducts();
  }, [filter]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/pos/products?filter=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const togglePushOnline = async (product) => {
    setUpdating(product._id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/pos/products/${product._id}/toggle-online`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_online_available: !product.is_online_available }),
        }
      );
      if (!response.ok) throw new Error('Failed to update');
      toast.success(
        !product.is_online_available ? 'Pushed to online store' : 'Removed from online store'
      );
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(null);
    }
  };

  const togglePosAvailable = async (product) => {
    setUpdating(product._id);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/pos/products/${product._id}/toggle-pos`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_pos_available: !product.is_pos_available }),
        }
      );
      if (!response.ok) throw new Error('Failed to update');
      toast.success('Product updated');
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdating(null);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase()) ||
      p.barcode?.includes(search)
  );

  return (
    <div className="space-y-6" data-testid="pos-admin-products">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500">Manage POS and online store products</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!window.confirm('Enable ALL products for Online store?')) return;
              try {
                const r = await fetch(`${API_URL}/api/admin/pos/products/bulk-toggle`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ scope: 'online', value: true }),
                });
                const d = await r.json();
                toast.success(`${d.updated} products enabled for Online`);
                fetchProducts();
              } catch (e) {
                toast.error('Bulk update failed');
              }
            }}
            data-testid="bulk-enable-online"
          >
            <Globe className="w-4 h-4 mr-2" />
            Push All to Online
          </Button>
          <Link to="/pos/admin/ai-onboard">
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Camera className="w-4 h-4 mr-2" />
              AI Scan New Product
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border shadow-sm flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by name, SKU, or barcode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="pos-products-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'All' },
            { value: 'pos_only', label: 'POS Only' },
            { value: 'online', label: 'Online' },
            { value: 'low_stock', label: 'Low Stock' },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.value
                  ? 'bg-slate-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-sm text-gray-600">
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">SKU / Barcode</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Stock</th>
                  <th className="px-4 py-3 font-medium">POS</th>
                  <th className="px-4 py-3 font-medium">Online</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredProducts.map((product) => (
                  <tr key={product._id} className="text-sm hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.images?.[0] && (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          {product.ai_generated && (
                            <span className="text-xs text-amber-600">AI-scanned</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{product.sku}</div>
                      {product.barcode && (
                        <div className="text-xs text-gray-400">{product.barcode}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">₹{product.price}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          (product.inventory || 0) > 10
                            ? 'bg-emerald-100 text-emerald-700'
                            : (product.inventory || 0) > 0
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {product.inventory || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={updating === product._id}
                        onClick={() => togglePosAvailable(product)}
                        data-testid={`toggle-pos-${product._id}`}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          product.is_pos_available !== false ? 'bg-amber-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            product.is_pos_available !== false ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={updating === product._id}
                        onClick={() => togglePushOnline(product)}
                        data-testid={`toggle-online-${product._id}`}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          product.is_online_available ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            product.is_online_available ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
